import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { replaceTermsTillDate } from '@/modules/promo';
import {
  MAX_WIN_SENTINEL,
  useMaxWinFromQuery,
} from '@/modules/promo/hooks/useMaxWinFromQuery';
import { useValidDate } from '@/modules/promo/hooks/useValidDateFromQuery';
import type { termsBgConfig } from './helpers';
import { ScTermsAndConditions } from './styled';

type TermItem = string | { text: string; items: string[] };

interface Props {
  title?: string;
  translation: string;
  terms?: TermItem[];
  withOpenBg?: boolean;
  validDate?: string;
  onClick?: () => void;
  onToggle?: (isOpen: boolean) => void;
  scrollOnOpen?: boolean;
  themeWithOpenBg?: keyof typeof termsBgConfig;
}

export const TermsAndConditions = ({
  title,
  translation,
  terms,
  withOpenBg,
  onClick,
  validDate,
  onToggle,
  scrollOnOpen = true,
  themeWithOpenBg,
}: Props) => {
  const { t } = useTranslation([translation, 'lpAssets']);

  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const validDateFromQuery = useValidDate(validDate);
  const maxWinMultiplier = useMaxWinFromQuery() ?? '5x';

  useEffect(() => {
    if (!(isOpen && scrollOnOpen)) return;
    const el = containerRef.current;
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY;
    window.scrollTo({ top, behavior: 'smooth' });
  }, [isOpen, scrollOnOpen]);

  const toggleOpen = () => {
    onClick?.();
    setIsOpen((prev) => {
      const newState = !prev;
      if (onToggle) onToggle(newState);
      return newState;
    });
  };

  const resolvedTerms: TermItem[] = (() => {
    if (terms) return terms;

    const rawValue = t('termsContent', {
      returnObjects: true,
      ...(validDateFromQuery ? { date: validDateFromQuery } : {}),
      maxWin: MAX_WIN_SENTINEL,
      maxWinNok: MAX_WIN_SENTINEL,
    });

    const toArray = (val: unknown): TermItem[] => {
      if (Array.isArray(val)) return val as TermItem[];
      if (typeof val === 'object' && val !== null)
        return Object.values(val) as TermItem[];
      return [];
    };
    const raw = toArray(rawValue);

    const strings = raw.filter(
      (item): item is string => typeof item === 'string'
    );
    const replaced = replaceTermsTillDate(strings, validDateFromQuery);
    const replacedByOriginal = new Map(
      strings.map((s, i) => [s, replaced[i] ?? s])
    );
    const withDates = raw.map((item) =>
      typeof item === 'string' ? (replacedByOriginal.get(item) ?? item) : item
    );

    const withMultiplier = (line: string) =>
      line.includes(MAX_WIN_SENTINEL)
        ? t('lpAssets:maxWin.multiplierAmount', {
            multiplier: maxWinMultiplier,
          })
        : line;

    return withDates.map((item) =>
      typeof item === 'string'
        ? withMultiplier(item)
        : {
            text: withMultiplier(item.text),
            items: item.items.map(withMultiplier),
          }
    );
  })();

  return (
    <ScTermsAndConditions
      bgScheme={themeWithOpenBg}
      isOpen={isOpen}
      ref={containerRef}
      withOpenBg={withOpenBg}
    >
      <div className="terms__title" onClick={toggleOpen}>
        <span>{title ? title : t('common:button.termsAndConditions')}</span>
        <img alt="Arrow" src={'/images/arrow.svg'} />
      </div>
      <div className="terms">
        <div className="terms-content">
          <ol>
            {resolvedTerms.map((item) =>
              typeof item === 'string' ? (
                <li key={item}>{item}</li>
              ) : (
                <li key={item.text}>
                  {item.text}
                  <ul>
                    {item.items.map((sub) => (
                      <li key={sub}>{sub}</li>
                    ))}
                  </ul>
                </li>
              )
            )}
          </ol>
        </div>
      </div>
    </ScTermsAndConditions>
  );
};
